import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from '../services/config.service';
import { LocalStorageUtil } from '../utils/local-storage.util';
import { tokenHasRole } from '../utils/jwt-role.util';
import { PendingMessageService } from '../services/pending-message.service';

function redirectToInternalLogin(router: Router) {
  return of(router.createUrlTree(['/login']));
}

function redirectToLoginWithMessage(router: Router, message: string) {
  return of(router.createUrlTree(['/login'], {
    queryParams: { authMessage: message }
  }));
}

const REPORTS_PREFIX = '/reports';
const LEADS_ROUTE_PREFIXES = [
  '/lead-simulation',
  '/dashboard',
  '/simulations',
  '/rebuying',
  '/approved-proposals',
  '/campaigns',
  '/quotas',
  '/sign-drive',
  '/insurance-services',
  '/leads-history',
  '/leads',
  '/calendar',
  '/admin-panel'
];

function isProtectedRoute(url: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => url === prefix || url.startsWith(`${prefix}/`) || url.startsWith(`${prefix}?`));
}

function deniedAreaMessage(area: 'Leads' | 'Reports'): string {
  return `Usuário sem permissão de acesso na área ${area}.`;
}

export const ssoAuthGuard: CanActivateFn = (_route, state) => {
  const platformId = inject(PLATFORM_ID);
  const configService = inject(ConfigService);
  const router = inject(Router);
  const pendingMessage = inject(PendingMessageService);
  const isDebugEnabled = configService.isDebugEnabled();

  const log = (message: string, details?: unknown) => {
    if (!isDebugEnabled) {
      return;
    }
    console.log(`[ssoAuthGuard] ${message}`, details ?? '');
  };

  const warn = (message: string, details?: unknown) => {
    if (!isDebugEnabled) {
      return;
    }
    console.warn(`[ssoAuthGuard] ${message}`, details ?? '');
  };

  log('Iniciando validação de rota', { url: state.url });

  if (!isPlatformBrowser(platformId)) {
    log('SSR detectado, guard liberado automaticamente');
    return true;
  }

  if (!LocalStorageUtil.hasAuthenticatedSession()) {
    warn('Sessão autenticada não encontrada. Limpando dados e redirecionando para /login', { url: state.url });
    LocalStorageUtil.clearAllVWData();
    return redirectToInternalLogin(router);
  }

  if (state.url.startsWith('/error')) {
    log('Rota /error liberada sem validações adicionais');
    return true;
  }

  const token = LocalStorageUtil.getAccessTokenOrDefault('');
  const hasReportsRole = tokenHasRole(token, 'Reports');
  const hasLeadsRole = tokenHasRole(token, 'Leads');

  const isReportsRoute = isProtectedRoute(state.url, [REPORTS_PREFIX]);
  const isLeadsRoute = isProtectedRoute(state.url, LEADS_ROUTE_PREFIXES);

  log('Resumo de autorização da rota', {
    url: state.url,
    isReportsRoute,
    isLeadsRoute,
    hasReportsRole,
    hasLeadsRole,
    phase2Enabled: configService.isPhase2Enabled()
  });

  if (isReportsRoute && !hasReportsRole) {
    if (hasLeadsRole) {
      warn('Acesso negado em Reports para usuário somente Leads. Redirecionando para /dashboard');
      pendingMessage.set(deniedAreaMessage('Reports'));
      return of(router.createUrlTree(['/dashboard']));
    }

    warn('Acesso negado em Reports sem role Leads/Reports. Limpando sessão e redirecionando para /login');
    LocalStorageUtil.clearAllVWData();
    return redirectToLoginWithMessage(router, 'Usuário sem permissão de acesso na Plataforma de Leads.');
  }

  if (isLeadsRoute && !hasLeadsRole) {
    if (hasReportsRole && configService.isPhase2Enabled()) {
      warn('Acesso negado em Leads para usuário somente Reports com phase2 ativo. Redirecionando para /reports');
      pendingMessage.set(deniedAreaMessage('Leads'));
      return of(router.createUrlTree(['/reports']));
    }

    warn('Acesso negado em Leads sem role válida. Limpando sessão e redirecionando para /login');
    LocalStorageUtil.clearAllVWData();
    return redirectToLoginWithMessage(router, 'Usuário sem permissão de acesso na Plataforma de Leads.');
  }

  log('Roles válidas para rota. Iniciando loadConfig()');
  return configService.loadConfig().pipe(
    map(() => {
      log('loadConfig() concluído com sucesso. Acesso permitido', { url: state.url });
      return true;
    }),
    catchError((error) => {
      warn('Falha em loadConfig() no guard', {
        url: state.url,
        status: error?.status,
        message: error?.message
      });

      if (error?.status === 401 || error?.status === 403) {
        warn('Erro 401/403 em loadConfig(). Limpando sessão e redirecionando para /login');
        LocalStorageUtil.clearAllVWData();
        return redirectToInternalLogin(router);
      }

      warn('Redirecionando para /error por falha de autenticação não tratada');
      void router.navigate(['/error'], {
        state: {
          message: error?.message || `Falha ao validar autenticação para acessar ${state.url}`,
        },
      });

      return of(false);
    })
  );
};

